import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Flex from '@utilities/components/Layout/components/Flex';
import { HelpIcon, IconButtonV2, toast } from '@utilities';

import { createChatSession, updateFlowStateTopicIds } from '@api/narrative-projects';
import { readFlow, softDeleteFlowTopic } from '@api/narratives';
import { narrativeStatus } from '@helpers/narratives';
import Narratives from '../Narratives';
import ConfirmApplyTemplateModal from './components/ConfirmApplyTemplateModal';
import ConfirmRemoveTopicModal from './components/ConfirmRemoveTopicModal';
import NarrativesCanvas from './components/NarrativesCanvas';
import NarrativesChatLandingPage from './components/NarrativesChatLandingPage';
import NarrativesChatWindow from './components/NarrativesChatWindow';
import NarrativesNavigation from './components/NarrativesNavigation';
import NarrativesTemplatesWindow from './components/NarrativesTemplatesWindow';
import NarrativesProjectHistory from './components/NarrativesProjectHistory';
import NarrativesTopicsWindow from './components/NarrativesTopicsWindow';
import { areSameOrderedIds, mergeTopicIds } from './utilities/helpers';

import { HELP_CENTER_BASE_URL } from '../Narratives/utilities/constants';
import { DEFAULT_DRAFT_NARRATIVE_NAME } from './utilities/constants';
import { getRunTopicComponentId } from './utilities/helpers';

import styles from './_index.module.scss';

const PAGES = {
  CHAT: 'chat',
  LANDING: 'landing',
  PAST_PROJECTS: 'past-projects',
  TEMPLATES: 'templates',
  TOPICS: 'topics',
  WORKSPACE_TEMPLATES: 'workspace-templates',
};

const WORKSPACE_PAGES = new Set([
  PAGES.CHAT,
  PAGES.PAST_PROJECTS,
  PAGES.TOPICS,
  PAGES.WORKSPACE_TEMPLATES,
]);

const buildInitialSessionState = () => ({
  chatSessionId: null,
});

const getTemplateTopicIds = (template) => {
  if (Array.isArray(template?.topicIds)) return template.topicIds;
  if (Array.isArray(template?.topic_ids)) return template.topic_ids;
  return [];
};

const getFlowComponentsSignature = (flowState) =>
  JSON.stringify(
    (Array.isArray(flowState?.components) ? flowState.components : [])
      .filter((componentId) => componentId != null)
      .map(String)
  );

const FLOW_FINISHED_STATUSES = new Set([narrativeStatus.COMPLETED, narrativeStatus.EXCEPTION]);

const NarrativesChat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePage, setActivePage] = useState(PAGES.LANDING);
  const [flowId, setFlowId] = useState(null);
  const [draftFlowState, setDraftFlowState] = useState(null);
  const [initialInput, setInitialInput] = useState('');
  const [projectNamesBySession, setProjectNamesBySession] = useState({});
  const [projectNarrativesBySession, setProjectNarrativesBySession] = useState({});
  const [narrativeNamesById, setNarrativeNamesById] = useState({});
  const [runFlowTopicIds, setRunFlowTopicIds] = useState(null);
  const [sessionState, setSessionState] = useState(buildInitialSessionState);
  const [selectedTopicCards, setSelectedTopicCards] = useState([]);
  const [chatSnapshot, setChatSnapshot] = useState(null);
  const [runTopicPendingDeletion, setRunTopicPendingDeletion] = useState(null);
  const [isDeletingRunTopic, setIsDeletingRunTopic] = useState(false);
  const [runFlowRefreshKey, setRunFlowRefreshKey] = useState(0);
  const [isLeftPaneCollapsed, setIsLeftPaneCollapsed] = useState(false);
  const [workspaceTemplateContext, setWorkspaceTemplateContext] = useState({
    activeTab: 'numerator',
    selectedTemplateId: null,
  });
  const [customTemplatesRefreshKey, setCustomTemplatesRefreshKey] = useState(0);
  const [lastCreatedCustomTemplate, setLastCreatedCustomTemplate] = useState(null);
  const [pendingTemplateSelection, setPendingTemplateSelection] = useState(null);
  const [isTemplateDirty, setIsTemplateDirty] = useState(true);
  const creatingChatSessionPromiseRef = useRef(null);
  const breakawaySyncRequestIdRef = useRef(0);
  const breakawayAddRequestIdRef = useRef(0);
  const templateApplyRequestIdRef = useRef(0);

  const isLanding = activePage === PAGES.LANDING;
  const isTemplates = activePage === PAGES.TEMPLATES;
  const isChat = activePage === PAGES.CHAT;
  const isTopics = activePage === PAGES.TOPICS;
  const isWorkspaceTemplates = activePage === PAGES.WORKSPACE_TEMPLATES;
  const isPastProjects = activePage === PAGES.PAST_PROJECTS;
  const shouldDisableBodyScroll = !isLanding && !isTemplates;
  const shouldShowWorkspace = isChat || isTopics || isPastProjects || isWorkspaceTemplates;
  const hasSelectedTopics = selectedTopicCards.length > 0;
  const hasWorkspaceContent = Boolean(flowId) || Boolean(draftFlowState) || hasSelectedTopics;
  const shouldAllowCanvasHydrationOnMount = !hasWorkspaceContent;
  const chatSessionIdFromUrl = searchParams.get('sessionId');
  const websocketFlows = useSelector((state) => state?.websocket?.flows);
  const activeRunFlow = useMemo(() => {
    if (!flowId || !Array.isArray(websocketFlows)) return null;
    return websocketFlows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  }, [flowId, websocketFlows]);

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(draftFlowState?.components)) return [];
    return draftFlowState.components.filter((componentId) => componentId != null);
  }, [draftFlowState]);

  const selectedTopicIds = useMemo(() => {
    const manualIds = selectedTopicCards.map((topic) => topic.id).filter((id) => id != null);
    if (flowId) {
      return flowStateTopicIds.length ? flowStateTopicIds : manualIds;
    }
    if (!flowStateTopicIds.length) {
      return manualIds;
    }
    return Array.from(new Set([...flowStateTopicIds, ...manualIds]));
  }, [flowId, flowStateTopicIds, selectedTopicCards]);
  const hasCanvasCards = useMemo(() => {
    if (flowId) return true;
    return selectedTopicIds.length > 0;
  }, [flowId, selectedTopicIds]);
  const activeChatSnapshot = useMemo(() => {
    if (!chatSnapshot) return null;
    if (chatSnapshot.chatSessionId !== sessionState.chatSessionId) return null;
    return chatSnapshot;
  }, [chatSnapshot, sessionState.chatSessionId]);

  const replaceChatSessionQueryParam = useCallback(
    (chatSessionId) => {
      const params = new URLSearchParams(searchParams);
      if (chatSessionId) {
        params.set('sessionId', chatSessionId);
      } else {
        params.delete('sessionId');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const resetSessionState = useCallback(() => {
    setSessionState(buildInitialSessionState());
    setChatSnapshot(null);
    setFlowId(null);
    setDraftFlowState(null);
    setRunFlowTopicIds(null);
    setIsDeletingRunTopic(false);
    setIsLeftPaneCollapsed(false);
    setRunFlowRefreshKey(0);
    setRunTopicPendingDeletion(null);
    setSelectedTopicCards([]);
    setWorkspaceTemplateContext({ activeTab: 'numerator', selectedTemplateId: null });
    setCustomTemplatesRefreshKey(0);
    setLastCreatedCustomTemplate(null);
    setPendingTemplateSelection(null);
    setNarrativeNamesById({});
    setProjectNamesBySession({});
    setProjectNarrativesBySession({});
    setIsTemplateDirty(true);
    replaceChatSessionQueryParam(null);
  }, [replaceChatSessionQueryParam]);

  const handleChatSessionReady = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      setSessionState((prev) => {
        if (prev.chatSessionId === sessionId) return prev;
        return { ...prev, chatSessionId: sessionId };
      });
      replaceChatSessionQueryParam(sessionId);
      setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
    },
    [replaceChatSessionQueryParam]
  );

  const handleFlowSelected = useCallback((nextFlowId) => {
    setFlowId(nextFlowId);
    setRunFlowTopicIds(null);
  }, []);

  const handleFlowStateChange = useCallback(
    (nextFlowState, options = {}) => {
      if (!options.shouldBreakAwayFromRun && nextFlowState == null) {
        setDraftFlowState(null);
        setIsTemplateDirty(true);
        return;
      }

      if (!options.shouldBreakAwayFromRun || !nextFlowState) {
        setDraftFlowState((previousFlowState) => {
          const normalizedFlowState = {
            ...(previousFlowState || {}),
            ...(nextFlowState || {}),
            narrativeName:
              nextFlowState?.narrativeName ||
              previousFlowState?.narrativeName ||
              DEFAULT_DRAFT_NARRATIVE_NAME,
          };

          const didComponentsChange =
            getFlowComponentsSignature(previousFlowState) !==
            getFlowComponentsSignature(normalizedFlowState);
          const didNarrativeNameChange =
            (previousFlowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME) !==
            (normalizedFlowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME);

          if (didComponentsChange || didNarrativeNameChange) {
            setIsTemplateDirty(true);
          }

          return normalizedFlowState;
        });
        return;
      }

      let nextDraftFlowState = nextFlowState;
      const fallbackRunTopicIds = Array.isArray(activeRunFlow?.topics)
        ? activeRunFlow.topics
            .filter((topic) => !topic?.isHidden)
            .map((topic) => getRunTopicComponentId(topic))
            .filter((componentId) => componentId != null)
        : null;
      const baseRunTopicIds = Array.isArray(runFlowTopicIds)
        ? runFlowTopicIds
        : fallbackRunTopicIds;

      if (Array.isArray(baseRunTopicIds) && Array.isArray(nextFlowState.components)) {
        const runComponentIdSet = new Set(baseRunTopicIds.map(String));
        const addedComponentIds = nextFlowState.components.filter(
          (componentId) => !runComponentIdSet.has(String(componentId))
        );
        const nextComponentIds = mergeTopicIds(baseRunTopicIds, addedComponentIds);

        nextDraftFlowState = { ...nextFlowState, components: nextComponentIds };

        if (
          sessionState.chatSessionId &&
          !areSameOrderedIds(nextFlowState.components, nextComponentIds)
        ) {
          const syncRequestId = ++breakawaySyncRequestIdRef.current;
          const expectedComponentsSignature = JSON.stringify(nextComponentIds.map(String));
          updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            topicIds: nextComponentIds,
          })
            .then((updatedFlowState) => {
              if (syncRequestId !== breakawaySyncRequestIdRef.current) return;
              setDraftFlowState((currentFlowState) => {
                const currentComponents = Array.isArray(currentFlowState?.components)
                  ? currentFlowState.components
                  : [];
                if (expectedComponentsSignature !== JSON.stringify(currentComponents.map(String))) {
                  return currentFlowState;
                }
                return updatedFlowState || nextDraftFlowState;
              });
            })
            .catch((error) => {
              console.error('Failed to sync draft flow state after chat breakaway', error);
              toast.error('We could not sync the draft topic cards. Please refresh and try again.');
            });
        }
      }

      setDraftFlowState((previousFlowState) => ({
        ...(previousFlowState || {}),
        ...(nextDraftFlowState || {}),
        narrativeName:
          nextDraftFlowState?.narrativeName ||
          previousFlowState?.narrativeName ||
          DEFAULT_DRAFT_NARRATIVE_NAME,
      }));
      setFlowId(null);
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);
    },
    [activeRunFlow, runFlowTopicIds, sessionState.chatSessionId]
  );

  const ensureChatSession = useCallback(async () => {
    if (sessionState.chatSessionId) return sessionState.chatSessionId;
    if (creatingChatSessionPromiseRef.current) return creatingChatSessionPromiseRef.current;

    const createPromise = (async () => {
      try {
        const sessionId = await createChatSession();
        if (!sessionId) throw new Error('Chat service did not return a session identifier.');
        handleChatSessionReady(sessionId);
        return sessionId;
      } catch (error) {
        console.error('Failed to create chat session from topics workspace', error);
        toast.error('We could not start your project. Please try again.');
        return null;
      } finally {
        creatingChatSessionPromiseRef.current = null;
      }
    })();

    creatingChatSessionPromiseRef.current = createPromise;
    return createPromise;
  }, [handleChatSessionReady, sessionState.chatSessionId]);

  const handleRunFlowTopicIdsChange = useCallback((nextTopicIds = []) => {
    setRunFlowTopicIds((previousTopicIds) => {
      if (!Array.isArray(previousTopicIds)) return nextTopicIds;

      return areSameOrderedIds(previousTopicIds, nextTopicIds) ? previousTopicIds : nextTopicIds;
    });
  }, []);

  const handleProjectNameSync = useCallback((chatSessionId, name) => {
    if (!chatSessionId || typeof name !== 'string') return;

    setProjectNamesBySession((previousNames) => {
      if (previousNames?.[chatSessionId] === name) return previousNames;
      return { ...previousNames, [chatSessionId]: name };
    });
  }, []);

  const handleProjectNarrativeCreated = useCallback(({ chatSessionId, flowId, name }) => {
    if (!chatSessionId || !flowId) return;

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const existingNarratives = Array.isArray(previousNarrativesBySession?.[chatSessionId])
        ? previousNarrativesBySession[chatSessionId]
        : [];

      const flowIdAsNumber = Number(flowId);
      const nextNarrative = {
        createdDatetime: null,
        id: flowIdAsNumber,
        isOwner: true,
        name: name || 'Untitled Narrative',
        status: 'RUNNING',
      };

      const existingIndex = existingNarratives.findIndex(
        (narrative) => Number(narrative?.id) === flowIdAsNumber
      );

      let nextNarratives;
      if (existingIndex > -1) {
        nextNarratives = [...existingNarratives];
        nextNarratives[existingIndex] = {
          ...nextNarratives[existingIndex],
          ...nextNarrative,
        };
      } else {
        nextNarratives = [nextNarrative, ...existingNarratives];
      }

      return {
        ...previousNarrativesBySession,
        [chatSessionId]: nextNarratives,
      };
    });
  }, []);

  const handleProjectNarrativeRenamed = useCallback(({ chatSessionId, flowId, name }) => {
    if (!chatSessionId || !flowId || typeof name !== 'string') return;
    setNarrativeNamesById((previousNamesById) => {
      if (previousNamesById?.[flowId] === name) return previousNamesById;
      return { ...previousNamesById, [flowId]: name };
    });

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const sessionNarratives = Array.isArray(previousNarrativesBySession?.[chatSessionId])
        ? previousNarrativesBySession[chatSessionId]
        : [];

      let didChange = false;
      const nextSessionNarratives = sessionNarratives.map((narrative) => {
        if (Number(narrative?.id) !== Number(flowId)) return narrative;
        if (narrative?.name === name) return narrative;
        didChange = true;
        return { ...narrative, name };
      });

      if (!didChange) return previousNarrativesBySession;

      return {
        ...previousNarrativesBySession,
        [chatSessionId]: nextSessionNarratives,
      };
    });
  }, []);

  const handleProjectDraftDeleted = useCallback(
    (chatSessionId) => {
      if (!chatSessionId || sessionState.chatSessionId !== chatSessionId) return;

      setDraftFlowState((previousFlowState) => {
        if (!previousFlowState) return previousFlowState;
        return {
          ...previousFlowState,
          components: [],
          narrativeName: '',
        };
      });
      if (flowId == null) {
        setSelectedTopicCards([]);
      }
    },
    [flowId, sessionState.chatSessionId]
  );

  useEffect(() => {
    if (!Array.isArray(websocketFlows) || websocketFlows.length === 0) return;
    const websocketFlowsById = new Map(
      websocketFlows.map((flow) => [Number(flow?.id), flow]).filter(([id]) => !Number.isNaN(id))
    );

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const nextNarrativesBySession = {};
      let didChange = false;

      Object.entries(previousNarrativesBySession).forEach(([chatSessionId, narratives]) => {
        if (!Array.isArray(narratives) || narratives.length === 0) {
          nextNarrativesBySession[chatSessionId] = narratives;
          return;
        }

        const nextNarratives = narratives.map((narrative) => {
          const matchedFlow = websocketFlowsById.get(Number(narrative?.id));
          if (!matchedFlow) return narrative;

          const nextStatus = matchedFlow?.status || narrative?.status;
          const nextNarrative = {
            ...narrative,
            createdDatetime: matchedFlow?.createdDatetime || narrative?.createdDatetime || null,
            name: matchedFlow?.name || narrative?.name || 'Untitled Narrative',
            status: nextStatus,
          };

          if (FLOW_FINISHED_STATUSES.has(nextStatus)) {
            nextNarrative.isOwner = Boolean(matchedFlow?.isOwner ?? matchedFlow?.is_owner);
          }

          const hasChanged =
            nextNarrative.createdDatetime !== narrative?.createdDatetime ||
            nextNarrative.name !== narrative?.name ||
            nextNarrative.status !== narrative?.status ||
            nextNarrative.isOwner !== narrative?.isOwner;

          if (hasChanged) {
            didChange = true;
          }

          return hasChanged ? nextNarrative : narrative;
        });

        nextNarrativesBySession[chatSessionId] = nextNarratives;
      });

      return didChange ? nextNarrativesBySession : previousNarrativesBySession;
    });
  }, [websocketFlows]);

  const breakAwayToDraftFlowState = useCallback(
    async ({ topic }) => {
      if (!topic?.id) return false;

      const chatSessionId = await ensureChatSession();
      if (!chatSessionId) return false;

      let baseTopicIds = [];
      if (Array.isArray(runFlowTopicIds)) {
        baseTopicIds = runFlowTopicIds;
      } else if (Array.isArray(activeRunFlow?.topics)) {
        baseTopicIds = activeRunFlow.topics
          .filter((topicItem) => !topicItem?.isHidden)
          .map((topicItem) => getRunTopicComponentId(topicItem))
          .filter((componentId) => componentId != null);
      } else if (Array.isArray(draftFlowState?.components)) {
        baseTopicIds = draftFlowState.components;
      }
      const nextComponentIds = mergeTopicIds(baseTopicIds, [topic.id]);
      const previousFlowState = draftFlowState;
      const previousFlowId = flowId;
      const previousSelectedTopicCards = selectedTopicCards;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, components: nextComponentIds }
        : { components: nextComponentIds };
      const requestId = ++breakawayAddRequestIdRef.current;

      setDraftFlowState(optimisticFlowState);
      setFlowId(null);
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        if (requestId !== breakawayAddRequestIdRef.current) return false;
        setDraftFlowState(updatedFlowState);
        return true;
      } catch (error) {
        if (requestId !== breakawayAddRequestIdRef.current) return false;
        console.error('Failed to create draft flow state from run Narrative', error);
        setDraftFlowState(previousFlowState);
        setFlowId(previousFlowId);
        setSelectedTopicCards(previousSelectedTopicCards);
        toast.error('We could not add that topic card. Please try again.');
        return false;
      }
    },
    [activeRunFlow, draftFlowState, ensureChatSession, flowId, runFlowTopicIds, selectedTopicCards]
  );

  const handleToggleTopicCard = useCallback(
    async (topic, shouldAdd) => {
      if (!topic?.id) return;

      const topicId = topic.id;

      if (shouldAdd) {
        if (flowId) {
          await breakAwayToDraftFlowState({ topic });
          return;
        }

        setSelectedTopicCards((prev) => {
          const exists = prev.some((item) => item.id === topicId);
          if (exists) return prev;

          const nextTopic = {
            id: topicId,
            name: topic.name,
            vizType: topic.vizType,
          };

          return [...prev, nextTopic];
        });
        setIsTemplateDirty(true);
        return;
      }

      if (flowId) {
        let runTopics = Array.isArray(activeRunFlow?.topics) ? activeRunFlow.topics : [];
        if (!runTopics.length) {
          try {
            const runFlowResponse = await readFlow({ id: flowId });
            runTopics = Array.isArray(runFlowResponse?.topics) ? runFlowResponse.topics : [];
          } catch (error) {
            console.error('Failed to load run topics for topic removal', error);
          }
        }
        const runTopic = runTopics.find(
          (item) => String(getRunTopicComponentId(item)) === String(topicId)
        );
        if (!runTopic?.id) {
          toast.error(
            'We could not find this topic card in the run. Please refresh and try again.'
          );
          return;
        }

        setRunTopicPendingDeletion({ runTopicId: runTopic.id, topicId });
        return;
      }

      setSelectedTopicCards((prev) => prev.filter((item) => item.id !== topicId));
      setIsTemplateDirty(true);

      const currentComponentIds = Array.isArray(draftFlowState?.components)
        ? draftFlowState.components
        : [];
      const nextComponentIds = currentComponentIds.filter((id) => String(id) !== String(topicId));

      if (nextComponentIds.length === currentComponentIds.length) return;

      const previousFlowState = draftFlowState;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, components: nextComponentIds }
        : {
            components: nextComponentIds,
            flowId,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
          };

      setDraftFlowState(optimisticFlowState);

      if (!sessionState.chatSessionId) return;

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            flowId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        setDraftFlowState(updatedFlowState);
      } catch (error) {
        console.error('Failed to remove topic card from draft flow state', error);
        setDraftFlowState(previousFlowState);
        toast.error('We could not remove that topic card. Please try again.');
      }
    },
    [activeRunFlow, breakAwayToDraftFlowState, draftFlowState, flowId, sessionState.chatSessionId]
  );

  const confirmRunTopicDeletion = useCallback(async () => {
    if (!runTopicPendingDeletion?.runTopicId) return;

    setIsDeletingRunTopic(true);
    try {
      await softDeleteFlowTopic({ topicId: runTopicPendingDeletion.runTopicId });
      let runTopics = Array.isArray(activeRunFlow?.topics) ? activeRunFlow.topics : [];
      if (!runTopics.length && flowId) {
        try {
          const runFlowResponse = await readFlow({ id: flowId });
          runTopics = Array.isArray(runFlowResponse?.topics) ? runFlowResponse.topics : [];
        } catch (error) {
          console.error('Failed to load run topics for topic sync after removal', error);
        }
      }

      const nextComponentIdsFromRun = runTopics
        .filter((topic) => String(topic?.id) !== String(runTopicPendingDeletion.runTopicId))
        .map((topic) => getRunTopicComponentId(topic))
        .filter((componentId) => componentId != null);
      const nextComponentIds =
        nextComponentIdsFromRun.length > 0
          ? nextComponentIdsFromRun
          : (Array.isArray(draftFlowState?.components) ? draftFlowState.components : []).filter(
              (componentId) => String(componentId) !== String(runTopicPendingDeletion.topicId)
            );

      const optimisticFlowState = draftFlowState
        ? { ...draftFlowState, components: nextComponentIds }
        : {
            components: nextComponentIds,
            flowId,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
          };
      setDraftFlowState(optimisticFlowState);

      if (sessionState.chatSessionId) {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            flowId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        setDraftFlowState(updatedFlowState);
      }

      setSelectedTopicCards((prev) =>
        prev.filter((item) => String(item?.id) !== String(runTopicPendingDeletion.topicId))
      );
      setRunFlowRefreshKey((prev) => prev + 1);
      setRunTopicPendingDeletion(null);
      setIsTemplateDirty(true);
    } catch (error) {
      console.error('Failed to remove run topic card from topics list', error);
      toast.error('We could not remove that topic card. Please try again.');
    } finally {
      setIsDeletingRunTopic(false);
    }
  }, [activeRunFlow, draftFlowState, flowId, runTopicPendingDeletion, sessionState.chatSessionId]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage === PAGES.TEMPLATES) {
        if (shouldShowWorkspace) {
          setActivePage(PAGES.WORKSPACE_TEMPLATES);
          setIsLeftPaneCollapsed(false);
          return;
        }

        setActivePage(PAGES.TEMPLATES);
        return;
      }

      if (nextPage === 'new-project') {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      if (nextPage === PAGES.LANDING) {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      const nextShowsWorkspace = WORKSPACE_PAGES.has(nextPage);
      if (nextShowsWorkspace) {
        if (activePage === nextPage && !isLeftPaneCollapsed) {
          setIsLeftPaneCollapsed(true);
          return;
        }

        setActivePage(nextPage);
        setIsLeftPaneCollapsed(false);
        return;
      }

      if (shouldShowWorkspace) {
        resetSessionState();
      }

      setActivePage(nextPage);
    },
    [activePage, isLeftPaneCollapsed, resetSessionState, shouldShowWorkspace]
  );

  const handleChatMessageSent = useCallback(() => {
    setInitialInput('');
  }, []);

  const handleLandingSubmit = (input) => setInitialInput(input);
  const handleHelpClick = () => {
    window.open(HELP_CENTER_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleChatStateSnapshot = useCallback((snapshot) => {
    setChatSnapshot(snapshot);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', shouldDisableBodyScroll);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [shouldDisableBodyScroll]);

  useEffect(() => {
    if (chatSessionIdFromUrl) {
      setSessionState((prev) => {
        if (prev.chatSessionId === chatSessionIdFromUrl) return prev;
        return { ...prev, chatSessionId: chatSessionIdFromUrl };
      });
      if (activePage === PAGES.LANDING) {
        setActivePage(PAGES.CHAT);
      }
      return;
    }

    if (!sessionState.chatSessionId) return;
    resetSessionState();
    setActivePage(PAGES.LANDING);
  }, [activePage, chatSessionIdFromUrl, resetSessionState, sessionState.chatSessionId]);

  useEffect(() => {
    if (shouldShowWorkspace) return;
    setFlowId(null);
    setDraftFlowState(null);
  }, [shouldShowWorkspace]);

  useEffect(() => {
    if (!hasSelectedTopics || sessionState.chatSessionId) return;
    ensureChatSession();
  }, [ensureChatSession, hasSelectedTopics, sessionState.chatSessionId]);

  const handleCloseToolWindow = useCallback(() => {
    setIsLeftPaneCollapsed(true);
  }, []);

  const handleTemplateSaved = useCallback((template) => {
    if (!template?.name) return;
    setLastCreatedCustomTemplate(template);
    setCustomTemplatesRefreshKey((previousKey) => previousKey + 1);
  }, []);

  const handleTemplateSelected = useCallback(
    async ({ activeTab = 'numerator', source = 'workspace', template }) => {
      const topicIds = getTemplateTopicIds(template);
      const requestId = ++templateApplyRequestIdRef.current;
      const optimisticFlowState = {
        components: topicIds,
        narrativeName: template?.name || DEFAULT_DRAFT_NARRATIVE_NAME,
      };
      const rollbackState = {
        draftFlowState,
        flowId,
        isTemplateDirty,
        selectedTopicCards,
      };

      let chatSessionId = sessionState.chatSessionId;

      try {
        const shouldCreateNewProject = source === 'full-page' && !shouldShowWorkspace;
        if (shouldCreateNewProject) {
          resetSessionState();
        }

        if (!chatSessionId) {
          chatSessionId = await ensureChatSession();
        }

        if (requestId !== templateApplyRequestIdRef.current) return;
        if (!chatSessionId) return;

        setWorkspaceTemplateContext({
          activeTab: activeTab === 'custom' ? 'custom' : 'numerator',
          selectedTemplateId: template?.id ?? null,
        });
        handleChatSessionReady(chatSessionId);
      } catch (error) {
        if (requestId !== templateApplyRequestIdRef.current) return;
        console.error('Failed to create chat session from template selection', error);
        toast.error('We could not start a new project. Please try again.');
        return;
      }

      setDraftFlowState(optimisticFlowState);
      setFlowId(null);
      setSelectedTopicCards([]);
      setActivePage(PAGES.WORKSPACE_TEMPLATES);
      setIsLeftPaneCollapsed(false);
      setPendingTemplateSelection(null);

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId,
            topicIds,
          })) || optimisticFlowState;

        if (requestId !== templateApplyRequestIdRef.current) return;
        setDraftFlowState({
          ...(updatedFlowState || {}),
          narrativeName:
            updatedFlowState?.narrativeName ||
            optimisticFlowState?.narrativeName ||
            DEFAULT_DRAFT_NARRATIVE_NAME,
        });
        setIsTemplateDirty(false);
      } catch (error) {
        if (requestId !== templateApplyRequestIdRef.current) return;
        console.error('Failed to hydrate draft flow state from template selection', error);
        toast.error('We could not load that template. Please try again.');
        setFlowId(rollbackState.flowId);
        setDraftFlowState(rollbackState.draftFlowState);
        setIsTemplateDirty(rollbackState.isTemplateDirty);
        setSelectedTopicCards(rollbackState.selectedTopicCards);
      }
    },
    [
      draftFlowState,
      ensureChatSession,
      handleChatSessionReady,
      isTemplateDirty,
      resetSessionState,
      selectedTopicCards,
      sessionState.chatSessionId,
      shouldShowWorkspace,
      flowId,
    ]
  );

  const requestTemplateSelection = useCallback(
    async (selection) => {
      if (!selection?.template) return;
      if (hasCanvasCards && isTemplateDirty) {
        setPendingTemplateSelection(selection);
        return;
      }
      await handleTemplateSelected(selection);
    },
    [handleTemplateSelected, hasCanvasCards, isTemplateDirty]
  );

  return (
    <>
      <Flex className={styles['narratives-root']} gap="none">
        <NarrativesNavigation
          activePage={activePage}
          isCollapsed={isLeftPaneCollapsed}
          onClick={handlePageChange}
        />

        {isLanding && (
          <>
            <NarrativesChatLandingPage
              onNavigation={handlePageChange}
              onSubmit={handleLandingSubmit}
            />

            <IconButtonV2
              aria-label="Help"
              className={styles['help-button']}
              icon={<HelpIcon />}
              onClick={handleHelpClick}
              size="small"
              tooltip="Help"
              tooltipProps={{ placement: 'left' }}
              variant="subtle"
            />
          </>
        )}

        {isTemplates && (
          <Narratives
            hasTemplateAccess
            onTemplateSelect={(selection) =>
              requestTemplateSelection({
                ...selection,
                source: 'full-page',
              })
            }
          />
        )}

        {shouldShowWorkspace && (
          <div className={styles['narratives-chat-shell']}>
            <div className={styles['narratives-chat-content']}>
              <div
                aria-hidden={isLeftPaneCollapsed}
                className={styles['narratives-chat-window']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-window"
                inert={isLeftPaneCollapsed ? '' : undefined}
              >
                {isChat && (
                  <NarrativesChatWindow
                    allowCanvasHydrationOnMount={shouldAllowCanvasHydrationOnMount}
                    chatSessionId={sessionState.chatSessionId}
                    initialErrorMessage={activeChatSnapshot?.errorMessage}
                    initialInput={initialInput}
                    initialInputValue={activeChatSnapshot?.inputValue}
                    initialMessages={activeChatSnapshot?.messages}
                    key={sessionState.chatSessionId || 'draft'}
                    onChatSessionReady={handleChatSessionReady}
                    onCloseToolWindow={handleCloseToolWindow}
                    onFlowSelected={handleFlowSelected}
                    onFlowStateChange={handleFlowStateChange}
                    onMessageSent={handleChatMessageSent}
                    onStateSnapshot={handleChatStateSnapshot}
                    shouldBreakAwayFromRunOnMessage={Boolean(flowId)}
                    shouldHydrateOnMount={!activeChatSnapshot?.wasHydrated}
                    syncCanvasOnMount={!activeChatSnapshot?.wasHydrated}
                  />
                )}
                {isTopics && (
                  <NarrativesTopicsWindow
                    onCloseToolWindow={handleCloseToolWindow}
                    onToggleTopicCard={handleToggleTopicCard}
                    selectedTopicIds={selectedTopicIds}
                  />
                )}
                {isWorkspaceTemplates && (
                  <NarrativesTemplatesWindow
                    customTemplatesRefreshKey={customTemplatesRefreshKey}
                    initialActiveTab={workspaceTemplateContext.activeTab}
                    initialActiveTemplateId={workspaceTemplateContext.selectedTemplateId}
                    lastCreatedCustomTemplate={lastCreatedCustomTemplate}
                    onCloseToolWindow={handleCloseToolWindow}
                    onSelectTemplate={(selection) =>
                      requestTemplateSelection({
                        ...selection,
                        source: 'workspace',
                      })
                    }
                  />
                )}
                {isPastProjects && (
                  <NarrativesProjectHistory
                    activeFlowId={flowId}
                    activeSessionId={sessionState.chatSessionId}
                    handleChatSessionReady={handleChatSessionReady}
                    handleFlowSelected={handleFlowSelected}
                    narrativeNamesById={narrativeNamesById}
                    onCloseToolWindow={handleCloseToolWindow}
                    onDraftDeleted={handleProjectDraftDeleted}
                    onProjectNameChange={handleProjectNameSync}
                    projectNamesBySession={projectNamesBySession}
                    projectNarrativesBySession={projectNarrativesBySession}
                  />
                )}
              </div>

              <div
                className={styles['narratives-chat-canvas']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-canvas"
              >
                <NarrativesCanvas
                  chatSessionId={sessionState.chatSessionId}
                  externalProjectName={projectNamesBySession?.[sessionState.chatSessionId]}
                  flowId={flowId}
                  flowState={draftFlowState}
                  onFlowSelected={handleFlowSelected}
                  onFlowStateChange={handleFlowStateChange}
                  onNarrativeCreated={handleProjectNarrativeCreated}
                  onNarrativeNameChange={handleProjectNarrativeRenamed}
                  onProjectNameChange={handleProjectNameSync}
                  onRunFlowTopicIdsChange={handleRunFlowTopicIdsChange}
                  onSelectedTopicsChange={setSelectedTopicCards}
                  onTemplateSaved={handleTemplateSaved}
                  runFlowRefreshKey={runFlowRefreshKey}
                  selectedTopicCards={selectedTopicCards}
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
      <ConfirmRemoveTopicModal
        isActive={Boolean(runTopicPendingDeletion)}
        isConfirmDisabled={isDeletingRunTopic}
        onCancel={() => setRunTopicPendingDeletion(null)}
        onConfirm={confirmRunTopicDeletion}
      />
      <ConfirmApplyTemplateModal
        isActive={Boolean(pendingTemplateSelection)}
        onCancel={() => setPendingTemplateSelection(null)}
        onConfirm={() => handleTemplateSelected(pendingTemplateSelection)}
      />
    </>
  );
};

export default NarrativesChat;
